import { useCallback, useEffect, useState } from 'react'
import type { ChainStatus, EventStore, PhotoStore, PinVault } from '@application/ports.ts'
import {
  getRuntime,
  isInstalled,
  isStoragePersisted,
  requestPersistentStorage,
} from '../runtime.ts'
import { es } from '../strings/es.ts'
import { BackupPanel } from './BackupPanel.tsx'
import { CheckRow, type CheckTone } from './CheckRow.tsx'
import { NotificationsPanel } from './NotificationsPanel.tsx'
import type { Aviso } from '@adapters/push/avisos.ts'
import { PinPanel } from './PinPanel.tsx'
import { usePwaUpdate } from '../pwa.ts'
import { versionLegible } from '../version.ts'
import { useInstalacion } from '../instalar.ts'
import { assistantAvailable, assistantConfig, readToken } from '@adapters/ai/config.ts'
import { pedirExplicacion } from '@adapters/ai/explicar.ts'

/**
 * Tres situaciones distintas, que antes decían las tres lo mismo: «busca en el
 * menú del navegador». Con nombre y en una tabla se leen de un vistazo, que es
 * más de lo que se puede decir de tres ternarios anidados en paralelo.
 */
type ComoVaLaInstalacion = 'puesta' | 'ofrecida' | 'sin-oferta'

const FILA_INSTALAR: Record<ComoVaLaInstalacion, { tone: CheckTone; value: string }> = {
  puesta: { tone: 'ok', value: es.checks.installedYes },
  ofrecida: { tone: 'info', value: es.checks.installedCan },
  'sin-oferta': { tone: 'warn', value: es.checks.installedNo },
}

/** Cómo va la prueba del maestro que explica. */
type Maestro =
  | { readonly fase: 'sin-probar' }
  | { readonly fase: 'probando' }
  | { readonly fase: 'dicho'; readonly texto: string; readonly ok: boolean }

interface Snapshot {
  readonly store: EventStore
  readonly photos: PhotoStore
  readonly vault: PinVault
  readonly events: number
  readonly chain: ChainStatus
  readonly pinSet: boolean
  readonly persisted: boolean
  readonly deviceId: string
}

export function SystemScreen({
  avisos = [],
  onBack,
}: {
  /** Las horas acordadas, sacadas del registro. Las pone cada hijo, no la tablet. */
  avisos?: readonly Aviso[]
  onBack?: () => void
}) {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const [failed, setFailed] = useState(false)
  const [online, setOnline] = useState(navigator.onLine)
  const update = usePwaUpdate()
  const instalacion = useInstalacion()
  const [instalado, setInstalado] = useState<string | null>(null)
  const [maestro, setMaestro] = useState<Maestro>({ fase: 'sin-probar' })

  const load = useCallback(async (): Promise<void> => {
    try {
      const runtime = await getRuntime()
      const [events, chain, pin, persisted] = await Promise.all([
        runtime.store.count(),
        runtime.store.verify(),
        runtime.vault.state(),
        isStoragePersisted().catch(() => false),
      ])
      setSnapshot({
        store: runtime.store,
        photos: runtime.photos,
        vault: runtime.vault,
        events,
        chain,
        pinSet: pin.isSet,
        persisted,
        deviceId: runtime.store.deviceId(),
      })
      setFailed(false)
    } catch (cause) {
      console.error('No se pudo cargar el estado del sistema', cause)
      setFailed(true)
    }
  }, [])

  useEffect(() => {
    // The first paint never waits on a permission prompt: asking the browser to
    // keep our storage is useful, but a tablet that shows "Comprobando" forever
    // because a promise did not settle is worse than no answer at all.
    let cancelled = false
    void (async () => {
      await load()
      if (cancelled) return
      await requestPersistentStorage().catch(() => false)
      if (cancelled) return
      await load()
    })()

    const goOnline = (): void => {
      setOnline(true)
    }
    const goOffline = (): void => {
      setOnline(false)
    }
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      cancelled = true
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [load])

  const installed = isInstalled()
  const hayServidor = assistantAvailable()
  const hayClave = (readToken() ?? '').length > 0
  const maestroVa = maestro.fase === 'dicho' && maestro.ok

  /**
   * Prueba de verdad: una cuenta trivial, de punta a punta.
   *
   * Es la única forma de distinguir «falta la clave» de «el modelo no contesta»
   * sin abrir una consola, y desde la pantalla del niño las dos se ven igual —
   * «ahora no se puede explicar»—, que para él está bien y para quien tiene que
   * arreglarlo no dice nada.
   */
  const probarMaestro = async (): Promise<void> => {
    setMaestro({ fase: 'probando' })
    const hecho = await pedirExplicacion(
      assistantConfig(),
      { destreza: 'sumar llevando', pide: '2 + 2', solucion: '4' },
      3,
    )
    setMaestro({
      fase: 'dicho',
      ok: hecho.ok,
      texto: hecho.ok ? es.checks.maestroVale : es.assistant.failures[hecho.failure],
    })
  }
  const comoVaLaInstalacion: ComoVaLaInstalacion = installed
    ? 'puesta'
    : instalacion.puede
      ? 'ofrecida'
      : 'sin-oferta'

  return (
    <div className="mx-auto max-w-3xl px-5 pt-8 pb-16">
      {onBack ? (
        <button type="button" onClick={onBack} className="text-muted mb-5 text-sm font-bold">
          {es.today.back}
        </button>
      ) : null}
      {update.ready ? (
        <div role="alert" className="bg-gold-soft border-gold mb-6 rounded border-t-4 p-4">
          <p className="m-0 font-bold">{es.update.available}</p>
          <p className="text-muted m-0 mt-1 text-sm">{es.update.explain}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className="bg-teal rounded px-5 font-bold text-white"
              onClick={update.apply}
            >
              {es.update.apply}
            </button>
            <button
              type="button"
              className="border-rule rounded border px-5 font-bold"
              onClick={update.dismiss}
            >
              {es.update.later}
            </button>
          </div>
        </div>
      ) : null}

      <header className="border-rule mb-8 border-b pb-6">
        <p className="text-teal m-0 text-xs font-bold tracking-[0.12em] uppercase">
          {es.shell.phaseLabel}
        </p>
        <h1 className="mt-3 mb-0 text-5xl leading-none font-extrabold tracking-tight">
          {es.app.name}
        </h1>
        <p className="text-muted mt-3 mb-0 max-w-prose">{es.shell.phaseExplain}</p>
      </header>

      <section className="border-rule bg-surface rounded border p-5">
        <div className="mb-2 flex items-baseline justify-between gap-4">
          <h2 className="m-0 text-lg font-bold">{es.shell.systemTitle}</h2>
          <button
            type="button"
            className="text-teal min-h-0 text-sm font-bold underline underline-offset-4"
            onClick={() => {
              void load()
            }}
          >
            {es.shell.refresh}
          </button>
        </div>

        {snapshot === null ? (
          failed ? (
            <div role="alert" className="py-4">
              <p className="text-coral m-0 font-bold">{es.shell.loadFailed}</p>
              <p className="text-muted m-0 mt-1 text-sm">{es.shell.loadFailedHint}</p>
            </div>
          ) : (
            <p className="text-muted m-0 py-4">{es.shell.checking}</p>
          )
        ) : (
          <>
            <CheckRow
              label={es.checks.installed}
              tone={FILA_INSTALAR[comoVaLaInstalacion].tone}
              value={FILA_INSTALAR[comoVaLaInstalacion].value}
              {...(comoVaLaInstalacion === 'sin-oferta'
                ? { hint: `${es.checks.installedNope} ${es.checks.installedHint}` }
                : {})}
            >
              {comoVaLaInstalacion === 'ofrecida' ? (
                <button
                  type="button"
                  className="bg-teal rounded px-5 font-bold text-white"
                  onClick={() => {
                    void instalacion.instalar().then((aceptado) => {
                      if (aceptado) setInstalado(es.checks.installedDone)
                    })
                  }}
                >
                  {es.checks.installedDo}
                </button>
              ) : null}
              {comoVaLaInstalacion === 'ofrecida' ? (
                <p className="text-muted m-0 mt-2 text-sm">{es.checks.installedProof}</p>
              ) : null}
              {comoVaLaInstalacion === 'sin-oferta' ? (
                <p className="text-muted m-0 text-sm">{es.checks.installedIos}</p>
              ) : null}
              {instalado ? (
                <p role="status" className="text-mint mt-2 mb-0 text-sm font-bold">
                  {instalado}
                </p>
              ) : null}
            </CheckRow>
            <CheckRow
              label={es.checks.offline}
              tone={update.offlineReady || installed ? 'ok' : 'info'}
              value={update.offlineReady || installed ? es.checks.offlineYes : es.checks.offlineNo}
              hint={es.checks.offlineHint}
            />
            <CheckRow
              label={es.checks.storage}
              tone={snapshot.persisted ? 'ok' : 'warn'}
              value={snapshot.persisted ? es.checks.storageYes : es.checks.storageNo}
              hint={es.checks.storageHint}
            />
            <CheckRow
              label={es.checks.connection}
              tone="info"
              value={online ? es.checks.online : es.checks.offlineNow}
            />
            <CheckRow
              label={es.checks.log}
              tone={snapshot.chain.ok ? 'ok' : 'warn'}
              value={es.checks.logCount(snapshot.events)}
              hint={
                snapshot.chain.ok
                  ? es.checks.chainOk
                  : es.checks.chainBroken(snapshot.chain.brokenAt ?? 0)
              }
            />
            <CheckRow
              label={es.checks.maestro}
              tone={maestroVa ? 'ok' : hayClave ? 'info' : 'warn'}
              value={
                !hayServidor
                  ? es.checks.maestroSinServidor
                  : !hayClave
                    ? es.checks.maestroSinClave
                    : maestro.fase === 'sin-probar'
                      ? es.checks.maestroSinProbar
                      : maestro.fase === 'probando'
                        ? es.checks.maestroProbando
                        : maestro.texto
              }
              {...(hayServidor && !hayClave ? { hint: es.checks.maestroSinClaveHint } : {})}
            >
              {hayServidor ? (
                <button
                  type="button"
                  className="border-rule rounded border px-5 font-bold disabled:opacity-60"
                  disabled={maestro.fase === 'probando'}
                  onClick={() => {
                    void probarMaestro()
                  }}
                >
                  {es.checks.maestroProbar}
                </button>
              ) : null}
            </CheckRow>
            <CheckRow
              label={es.checks.version}
              tone="info"
              value={versionLegible()}
              hint={es.checks.versionHint}
            />
            <CheckRow label={es.checks.device} tone="info" value={snapshot.deviceId} />
            <CheckRow
              label={es.checks.pin}
              tone={snapshot.pinSet ? 'ok' : 'warn'}
              value={snapshot.pinSet ? es.checks.pinSet : es.checks.pinMissing}
            >
              <PinPanel
                vault={snapshot.vault}
                isSet={snapshot.pinSet}
                onChanged={() => {
                  void load()
                }}
              />
            </CheckRow>
          </>
        )}
      </section>

      <section className="border-teal bg-teal-soft mt-8 rounded-xl border-t-4 p-5">
        <h2 className="m-0 mb-2 text-base font-extrabold">🔒 {es.privacy.title}</h2>
        <p className="m-0 font-bold">{es.privacy.body}</p>
        <p className="m-0 mt-1">{es.privacy.consequence}</p>
      </section>

      <NotificationsPanel avisos={avisos} />

      {snapshot ? (
        <BackupPanel
          store={snapshot.store}
          photos={snapshot.photos}
          yaHayCasa={snapshot.events > 0}
          onChanged={() => {
            void load()
          }}
        />
      ) : null}
    </div>
  )
}

export type { CheckTone }
